import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import _ from 'lodash';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { EMPLOYEE_PAY_CSV_FORM } from 'src/constants/FormNames';
import { ENCODING_OPTIONS } from 'src/constants/Options';
import { FileField } from 'src/jbc-front/components/Form';
import Button from 'jbc-front/components/Button';
import { FormSubmitBtn } from 'src/buttons';
import {
  downloadTemplateCsv,
  importEmployeePayCsv,
  getDownloadPayCsvJobs,
  getImportPayCsvJobs,
  getCompletedDownloadPayCsvJob,
  setCompletedDownloadPayCsvJob,
  fetchImportPayCsvJobs,
  fetchDownloadPayCsvJobs
} from 'src/reducers/employees/employeePays';
import Download from 'src/icons/Download';
import { formatDateWithHour } from 'src/utils/Date';
import { redirectTo } from 'src/utils/Http';
import DownloadCsvEncodingRadioButton from 'src/components/DownloadCsvEncodingRadioButton';
import { MAX_POLLING_INTERVAL, UTF_8 } from 'src/constants/Generals';

import styles from './EmployeePayDownloadCsvForm.scss';

export const findLatestCompletedPayPayCsvJob = (jobInformation, downloadCsvJob, completedJob) => {
  if ((_.isEmpty(downloadCsvJob) || _.isEmpty(jobInformation)) && _.isEmpty(completedJob)) {
    return null;
  }
  let successes = _.get(downloadCsvJob, 'success', []);
  successes = _.orderBy(successes, ['id'], ['desc']);
  const job = _.find(successes, o => o.jobInfo === jobInformation && !_.isEmpty(o.downloadLink));
  const completedJobs = _.compact([job, completedJob]);
  return completedJobs.length > 0 ? _.maxBy(completedJobs, j => j.id) : null;
};

// Get the string of the last updated date and time.
export const getLastCompletedDate = payCsvJob => {
  if (_.isEmpty(payCsvJob) || _.isEmpty(payCsvJob.endAt)) {
    return null;
  }
  return formatDateWithHour(payCsvJob.endAt);
};

export const EmployeePayCsvInsideForm = ({
  handleSubmit,
  submitting,
  pristine,
  submit,
  createCsv,
  downloadPayCsvJobs,
  importPayCsvJobs,
  jobInfo,
  completedDownloadPayCsvJob,
  updateCompletedDownloadPayCsvJob,
  fetchDownloadCsvJob,
  fetchImportCsvJob
}) => {
  const [isPollingCsvDownloadStatus, setIsPollingCsvDownloadStatus] = useState(false);
  const { t } = useTranslation();

  const pollingCsvImportStatus = interval => {
    let newInterval = interval;
    if ((importPayCsvJobs && Object.keys(importPayCsvJobs).includes('waiting')) || submitting) {
      fetchImportCsvJob();
      if (newInterval < MAX_POLLING_INTERVAL) {
        newInterval *= 2;
      }
    }
    setTimeout(pollingCsvImportStatus, newInterval, newInterval);
  };

  const pollingCsvDownloadStatus = interval => {
    let newInterval = interval;
    if ((downloadPayCsvJobs && Object.keys(downloadPayCsvJobs).includes('waiting')) || isPollingCsvDownloadStatus) {
      fetchDownloadCsvJob();
      if (newInterval < MAX_POLLING_INTERVAL) {
        newInterval *= 2;
      }
    }
    setIsPollingCsvDownloadStatus(false);
    setTimeout(pollingCsvDownloadStatus, newInterval, newInterval);
  };

  useEffect(() => {
    fetchImportCsvJob();
    fetchDownloadCsvJob();
  }, []);

  useEffect(
    () => {
      if (submitting) {
        pollingCsvImportStatus(1000);
      }
      if (isPollingCsvDownloadStatus) {
        pollingCsvDownloadStatus(1000);
        setIsPollingCsvDownloadStatus(false);
      }
    },
    [submitting, isPollingCsvDownloadStatus]
  );

  const downloadCsv = downloadPath => {
    if (_.isEmpty(downloadPath)) {
      return;
    }
    redirectTo(downloadPath);
  };

  const latestCompletedJob = findLatestCompletedPayPayCsvJob(jobInfo, downloadPayCsvJobs, completedDownloadPayCsvJob);
  if (latestCompletedJob) {
    updateCompletedDownloadPayCsvJob(latestCompletedJob);
  }

  const hasDownloadInprogressJobs = !_.isEmpty(downloadPayCsvJobs.waiting) || !_.isEmpty(downloadPayCsvJobs.inProgress);
  const hasImportInprogressJobs = !_.isEmpty(importPayCsvJobs.waiting) || !_.isEmpty(importPayCsvJobs.inProgress);
  const disabledCreateBtn = hasDownloadInprogressJobs || hasImportInprogressJobs;
  const disabledDownloadBtn = _.isEmpty(latestCompletedJob);
  const disabledUploadBtn = hasDownloadInprogressJobs || hasImportInprogressJobs;
  const DisabledReason = (
    <ul>
      <li>{t('features.EmployeePays.components.EmployeePayDownloadCsvForm.creating_data_please_wait')}</li>
      <li>{t('features.EmployeePays.components.EmployeePayDownloadCsvForm.downloadable_file_sent_to_email_after_completed')}</li>
    </ul>
  );
  const lastCompletedDate = getLastCompletedDate(latestCompletedJob);

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>{t('features.EmployeePays.components.EmployeePayDownloadCsvForm.upload_process')}</div>
      <div className={styles.body}>
        <form onSubmit={handleSubmit}>
          <dl className={styles.label}>
            <dt className={styles.number}>1</dt>
            <dd className={styles.detail}>
              <p className={styles.text}>{t('features.EmployeePays.components.EmployeePayDownloadCsvForm.create_salary_data_CSV')}</p>
              <p className={styles.caution}>
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.warning')}
                <br />
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.if_salary_information_changed_after_creation_always_update_CSV_data_by_pushing_create_salary_data_CSV_button')}
              </p>
              <div className="u-mt30">
                <DownloadCsvEncodingRadioButton name={'encoding'} label={null} options={ENCODING_OPTIONS} required={false} />
              </div>
              <Button
                onClick={() => {
                  createCsv();
                  setIsPollingCsvDownloadStatus(true);
                }}
                widthWide
                disabled={disabledCreateBtn}
                disabledReason={DisabledReason}
              >
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.create_salary_data_csv')}
              </Button>
              {lastCompletedDate && (
                <p className={classNames('u-mt15', styles.lastUpdate)}>
                  {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.lastest_salary_data_CSV_creation_update')}
                  {lastCompletedDate}
                </p>
              )}
            </dd>
          </dl>

          <dl className={styles.label}>
            <dt className={styles.number}>2</dt>
            <dd className={styles.detail}>
              <p className={styles.text}>
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.download_salary_data_CSV_created_in_1')}
              </p>
              <Button onClick={() => downloadCsv(_.get(latestCompletedJob, 'downloadLink', null))} widthWide disabled={disabledDownloadBtn}>
                <span className={styles.downloadIcon}>
                  <Download size={14} />
                </span>
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.download_salary_data_CSV')}
              </Button>
            </dd>
          </dl>

          <dl className={styles.label}>
            <dt className={styles.number}>3</dt>
            <dd className={styles.detail}>
              {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.change_provision_and_deduction_items_in_downloaded_CSV_file_as_needed_using_explanation_below_as_reference')}
              <p className={styles.caution}>
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.warning')}
                <br />
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.leave_automatically_calcuated_provision_and_deduction_items_such_as_insurance_premiums_blank')}
                <br />
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.items_with_input_value_will_not_be_automatically_calculated')}
                <br />
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.click_back_to_before_manual_input_button_on_salary_edit_page_to_calculate_automatically_again')}
              </p>
            </dd>
          </dl>

          <dl className={styles.label}>
            <dt className={styles.number}>4</dt>
            <dd className={styles.detail}>
              <p className={styles.text}>
                {t('features.EmployeePays.components.EmployeePayDownloadCsvForm.select_completed_file_and_upload')}
              </p>
              <FileField
                label={t('features.EmployeePays.components.EmployeePayDownloadCsvForm.data_file_of_provision_and_deduction_items')}
                name="employee_pay_csv"
                accepted="csv"
                required
              />
            </dd>
          </dl>
          <FormSubmitBtn
            text={t('features.EmployeePays.components.EmployeePayDownloadCsvForm.upload')}
            {...{ submitting, pristine, submit }}
            widthWide
            disabled={disabledUploadBtn || submitting}
          />
        </form>
      </div>
    </div>
  );
};

const EmployeePayDownloadCsvForm = reduxForm({
  form: EMPLOYEE_PAY_CSV_FORM
})(EmployeePayCsvInsideForm);

const mapStateToProps = (state, ownProps) => ({
  initialValues: { encoding: UTF_8, jobInfo: ownProps.jobInfo },
  downloadPayCsvJobs: getDownloadPayCsvJobs(state) || {},
  importPayCsvJobs: getImportPayCsvJobs(state) || {},
  completedDownloadPayCsvJob: getCompletedDownloadPayCsvJob(state) || null
});
const mapDispatchToProps = dispatch => ({
  onSubmit: data => dispatch(importEmployeePayCsv(data)),
  createCsv: () => dispatch(downloadTemplateCsv()),
  updateCompletedDownloadPayCsvJob: job => {
    dispatch(setCompletedDownloadPayCsvJob(job));
  },
  fetchImportCsvJob: () => dispatch(fetchImportPayCsvJobs(true)),
  fetchDownloadCsvJob: () => dispatch(fetchDownloadPayCsvJobs(true))
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeePayDownloadCsvForm);
